import { createHttpClient } from '@adapters/HttpClient';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { ProductApiAdapter } from '@shared/adapters/ProductApiAdapter';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { apiClientWH } from '@shared/services/ApiClient';
import { ProductService } from '@shared/services/ProductService';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import { FiltersApiAdapter } from '../adapters/FiltersApiAdapter';
import { TeamsApiAdapter } from '../adapters/TeamsApiAdapter';

// Estados logísticos estáticos
const LOGISTIC_STATUS_OPTIONS = [
  { value: '1', label: 'Sin estado logístico' },
  { value: '2', label: 'En Tránsito' },
  { value: '3', label: 'En Reparto' },
  { value: '4', label: 'Punto de recogida' },
  { value: '5', label: 'Entregado' },
  { value: '6', label: 'Devuelto al Remitente' },
  { value: '7', label: 'Cancelado' },
  { value: '8', label: 'Siniestro' },
  { value: '9', label: 'Reexpedido' },
  { value: '10', label: 'Destruido' },
];

interface OrdersFiltersState {
  processedAt: DateRange | undefined;
  createdAt: DateRange | undefined;
  trackingUpdateAt: DateRange | undefined;
  logisticStatus: string[];
  shop: string[];
  paymentMethod: string[];
  products: string[];
  courier: string[];
  client: string[];
  country: string[];
  stockLocation: string[];
  shippingPhone: string;
  hasOrderErrors: boolean;
  generalErrorIds: string[];
}

interface UseOrdersFiltersProps {
  /** Condiciones iniciales desde query params */
  initialConditions?: FilterConditionOutput[];
  /** Callback cuando cambian los filtros */
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** Si mostrar el filtro de cliente */
  hasTeamData?: boolean;
}

/**
 * Hook para gestionar los filtros simplificados de Orders
 *
 * Convierte el estado de los filtros UI al formato FilterConditionOutput[]
 * esperado por la API y gestiona la carga de opciones dinámicas.
 */
export const useOrdersFilters = ({ initialConditions = [], onChange, hasTeamData = false }: UseOrdersFiltersProps) => {
  const { t } = useTranslation('ordersModule');

  // Estado de los filtros
  const [filters, setFilters] = useState<OrdersFiltersState>(() => {
    // Parsear condiciones iniciales para establecer estado inicial
    return parseConditionsToState(initialConditions);
  });

  // HTTP Client y adapters
  const httpClient = createHttpClient(apiClientWH);
  const filtersAdapter = useMemo(() => new FiltersApiAdapter(httpClient), [httpClient]);
  const teamsAdapter = useMemo(() => new TeamsApiAdapter(httpClient), [httpClient]);

  // Servicio de productos para búsqueda con paginación
  const productService = useMemo(() => {
    const adapter = new ProductApiAdapter(httpClient);
    return new ProductService(adapter);
  }, [httpClient]);

  // Cargar opciones dinámicas
  const { data: shops = [], isLoading: isLoadingShops } = useQuery({
    queryKey: ['orders-filters-shops', hasTeamData],
    queryFn: async () => {
      const shops = await filtersAdapter.getShops({ includeTeam: hasTeamData });
      return shops.map((shop) => {
        // Construir descripción con nombre del team si aplica
        const description = hasTeamData && shop.team?.name ? shop.team.name : undefined;
        return {
          value: shop.id.toString(),
          label: shop.name,
          description,
        };
      });
    },
  });

  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['orders-filters-payment-methods'],
    queryFn: async () => {
      const methods = await filtersAdapter.getPaymentMethods();
      return methods.map((method) => ({ value: method.id.toString(), label: method.name }));
    },
  });

  const { data: couriers = [], isLoading: isLoadingCouriers } = useQuery({
    queryKey: ['orders-filters-couriers'],
    queryFn: async () => {
      const couriers = await filtersAdapter.getCouriers();
      return couriers.map((courier) => ({ value: courier.id.toString(), label: courier.name }));
    },
  });

  const { data: clients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['orders-filters-clients'],
    queryFn: async () => {
      if (!hasTeamData) return [];
      const teams = await teamsAdapter.getTeamsChild();
      return teams.map((team) => ({ value: team.id.toString(), label: team.name }));
    },
    enabled: hasTeamData,
  });

  // Cargar países
  const { data: countries = [], isLoading: isLoadingCountries } = useQuery({
    queryKey: ['orders-filters-countries'],
    queryFn: async () => {
      const countries = await filtersAdapter.getCountries();
      return countries.map((country) => ({ value: country.id.toString(), label: country.name }));
    },
  });

  const { data: generalErrorsRaw = [], isLoading: isLoadingGeneralErrors } = useQuery({
    queryKey: ['orders-filters-general-errors'],
    queryFn: () => filtersAdapter.getGeneralErrors(),
  });

  const generalErrors = useMemo(
    () =>
      generalErrorsRaw.map((error) => ({
        value: error.id,
        label: error.error_key ? String(t(`errors.${error.error_key}`, error.name || error.error_key)) : error.name,
      })),
    [generalErrorsRaw, t],
  );

  // Función para cargar productos con paginación y búsqueda (para MultiSelect dinámico)
  const loadProductsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await productService.getProductsPaginated({
          searchTerm: params.search,
          page: params.page,
          limit: params.limit,
          includeTeam: hasTeamData, // Incluir team solo en 3PL (incompatible con fields: basic)
          ...(!hasTeamData ? { fields: 'basic' as const } : {}),
        });

        const options = result.products.map((product) => {
          // Construir descripción: código + nombre del team (si aplica)
          const parts: string[] = [];
          if (product.code) parts.push(product.code);
          if (hasTeamData && product.team?.name) parts.push(product.team.name);

          return {
            value: product.id.toString(),
            label: product.name || `Producto ${product.id}`,
            description: parts.length > 0 ? parts.join(' · ') : undefined,
          };
        });

        return {
          options,
          hasMore: result.hasMore,
        };
      } catch (error) {
        console.error('Error loading products:', error);
        return { options: [], hasMore: false };
      }
    },
    [productService, hasTeamData],
  );

  // Función para cargar preparaciones con paginación y búsqueda (para MultiSelect dinámico)
  const loadPreparationsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await filtersAdapter.getPreparationsPaginated({
          search: params.search,
          page: params.page,
          limit: params.limit,
        });

        const options = result.preparations.map((prep) => ({
          value: prep.id,
          label: prep.name,
        }));

        return {
          options,
          hasMore: result.hasMore,
        };
      } catch (error) {
        console.error('Error loading preparations:', error);
        return { options: [], hasMore: false };
      }
    },
    [filtersAdapter],
  );

  // Función para cargar tiendas con paginación y búsqueda (para MultiSelect dinámico)
  const loadShopsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await filtersAdapter.getShopsPaginated({
          search: params.search,
          page: params.page,
          limit: params.limit,
          includeTeam: hasTeamData,
        });

        const options = result.shops.map((shop) => {
          // Construir descripción con nombre del team si aplica
          const description = hasTeamData && shop.team?.name ? shop.team.name : undefined;
          return {
            value: shop.id,
            label: shop.name,
            description,
          };
        });

        return {
          options,
          hasMore: result.hasMore,
        };
      } catch (error) {
        console.error('Error loading shops:', error);
        return { options: [], hasMore: false };
      }
    },
    [filtersAdapter, hasTeamData],
  );

  // Convertir estado a FilterConditionOutput[]
  const convertStateToConditions = useCallback((state: OrdersFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    // Processed_at (processed_at)
    if (state.processedAt?.from && state.processedAt?.to) {
      conditions.push({
        column: 'processed_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.processedAt.from),
      });
      conditions.push({
        column: 'processed_at',
        action: 'less_equal',
        value: formatDateForAPI(state.processedAt.to),
      });
    }

    // Created_at (order_created_at)
    if (state.createdAt?.from && state.createdAt?.to) {
      conditions.push({
        column: 'order_created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.createdAt.from),
      });
      conditions.push({
        column: 'order_created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.createdAt.to),
      });
    }

    // Estado logístico (shipment_status_id)
    if (state.logisticStatus.length > 0) {
      conditions.push({
        column: 'shipment_status_id',
        action: 'in',
        value: state.logisticStatus,
      });
    }

    // Tienda (shop_id)
    if (state.shop.length > 0) {
      conditions.push({
        column: 'shop_id',
        action: 'in',
        value: state.shop,
      });
    }

    // Método de pago (payment_method_id)
    if (state.paymentMethod.length > 0) {
      conditions.push({
        column: 'payment_method_id',
        action: 'in',
        value: state.paymentMethod,
      });
    }

    // Productos (product_id)
    if (state.products.length > 0) {
      conditions.push({
        column: 'product_id',
        action: 'in',
        value: state.products,
      });
    }

    // Courier (courier_id)
    if (state.courier.length > 0) {
      conditions.push({
        column: 'courier_id',
        action: 'in',
        value: state.courier,
      });
    }

    // Cliente (team_id)
    if (state.client.length > 0) {
      conditions.push({
        column: 'team_id',
        action: 'in',
        value: state.client,
      });
    }

    // País (country_id)
    if (state.country.length > 0) {
      conditions.push({
        column: 'country_id',
        action: 'in',
        value: state.country,
      });
    }

    // Ubicación de stock / Preparación (stock_location_orders_id)
    if (state.stockLocation.length > 0) {
      conditions.push({
        column: 'stock_location_orders_id',
        action: 'in',
        value: state.stockLocation,
      });
    }

    // Fecha última actualización tracking (date_tracking_update)
    if (state.trackingUpdateAt?.from && state.trackingUpdateAt?.to) {
      conditions.push({
        column: 'date_tracking_update',
        action: 'greater_equal',
        value: formatDateForAPI(state.trackingUpdateAt.from),
      });
      conditions.push({
        column: 'date_tracking_update',
        action: 'less_equal',
        value: formatDateForAPI(state.trackingUpdateAt.to),
      });
    }

    // Teléfono de envío (shipping_phone) - búsqueda parcial
    if (state.shippingPhone && state.shippingPhone.trim()) {
      conditions.push({
        column: 'shipping_phone',
        action: 'contains',
        value: state.shippingPhone.trim(),
      });
    }

    // Pedidos con errores (has_order_errors)
    if (state.hasOrderErrors) {
      conditions.push({
        column: 'has_order_errors',
        action: 'equal',
        value: '1',
      });
    }

    // Tipos de error (general_error_ids)
    if (state.generalErrorIds.length > 0) {
      conditions.push({
        column: 'general_error_ids',
        action: 'in',
        value: state.generalErrorIds,
      });
    }

    return conditions;
  }, []);

  // Actualizar filtros y notificar cambio
  const updateFilters = useCallback(
    (updates: Partial<OrdersFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        const conditions = convertStateToConditions(newState);
        onChange?.(conditions);
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  // Handlers individuales
  const handleProcessedAtChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ processedAt: range });
    },
    [updateFilters],
  );

  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ createdAt: range });
    },
    [updateFilters],
  );

  const handleLogisticStatusChange = useCallback(
    (values: string[]) => {
      updateFilters({ logisticStatus: values });
    },
    [updateFilters],
  );

  const handleShopChange = useCallback(
    (values: string[]) => {
      updateFilters({ shop: values });
    },
    [updateFilters],
  );

  const handlePaymentMethodChange = useCallback(
    (values: string[]) => {
      updateFilters({ paymentMethod: values });
    },
    [updateFilters],
  );

  const handleProductsChange = useCallback(
    (values: string[]) => {
      updateFilters({ products: values });
    },
    [updateFilters],
  );

  const handleCourierChange = useCallback(
    (values: string[]) => {
      updateFilters({ courier: values });
    },
    [updateFilters],
  );

  const handleClientChange = useCallback(
    (values: string[]) => {
      updateFilters({ client: values });
    },
    [updateFilters],
  );

  const handleCountryChange = useCallback(
    (values: string[]) => {
      updateFilters({ country: values });
    },
    [updateFilters],
  );

  const handleStockLocationChange = useCallback(
    (values: string[]) => {
      updateFilters({ stockLocation: values });
    },
    [updateFilters],
  );

  const handleShippingPhoneChange = useCallback(
    (value: string) => {
      updateFilters({ shippingPhone: value });
    },
    [updateFilters],
  );

  const handleTrackingUpdateAtChange = useCallback(
    (range: DateRange | undefined) => {
      updateFilters({ trackingUpdateAt: range });
    },
    [updateFilters],
  );

  const handleHasOrderErrorsChange = useCallback(
    (value: boolean) => {
      updateFilters({ hasOrderErrors: value });
    },
    [updateFilters],
  );

  const handleGeneralErrorIdsChange = useCallback(
    (values: string[]) => {
      updateFilters({ generalErrorIds: values });
    },
    [updateFilters],
  );

  // Limpiar todos los filtros
  const clearAllFilters = useCallback(() => {
    const emptyState: OrdersFiltersState = {
      processedAt: undefined,
      createdAt: undefined,
      trackingUpdateAt: undefined,
      logisticStatus: [],
      shop: [],
      paymentMethod: [],
      products: [],
      courier: [],
      client: [],
      country: [],
      stockLocation: [],
      shippingPhone: '',
      hasOrderErrors: false,
      generalErrorIds: [],
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  // Sincronizar con condiciones iniciales cuando cambien
  useEffect(() => {
    const newState = parseConditionsToState(initialConditions);
    setFilters(newState);
  }, [initialConditions]);

  // Obtener condiciones actuales
  const currentConditions = useMemo(() => convertStateToConditions(filters), [filters, convertStateToConditions]);

  return {
    // Estado de filtros
    filters,

    // Handlers
    handleProcessedAtChange,
    handleCreatedAtChange,
    handleTrackingUpdateAtChange,
    handleLogisticStatusChange,
    handleShopChange,
    handlePaymentMethodChange,
    handleProductsChange,
    handleCourierChange,
    handleClientChange,
    handleCountryChange,
    handleStockLocationChange,
    handleShippingPhoneChange,
    handleHasOrderErrorsChange,
    handleGeneralErrorIdsChange,
    clearAllFilters,

    // Opciones disponibles
    logisticStatusOptions: LOGISTIC_STATUS_OPTIONS,
    shopOptions: shops,
    paymentMethodOptions: paymentMethods,
    courierOptions: couriers,
    clientOptions: clients,
    countryOptions: countries,
    generalErrorOptions: generalErrors,
    loadProductsForMultiSelect,
    loadPreparationsForMultiSelect,
    loadShopsForMultiSelect,

    // Estados de carga
    isLoadingShops,
    isLoadingPaymentMethods,
    isLoadingCouriers,
    isLoadingClients,
    isLoadingCountries,
    isLoadingGeneralErrors,

    // Condiciones actuales en formato API
    currentConditions,
  };
};

/**
 * Parsea condiciones FilterConditionOutput[] al estado de filtros
 */
function parseConditionsToState(conditions: FilterConditionOutput[]): OrdersFiltersState {
  const state: OrdersFiltersState = {
    processedAt: undefined,
    createdAt: undefined,
    trackingUpdateAt: undefined,
    logisticStatus: [],
    shop: [],
    paymentMethod: [],
    products: [],
    courier: [],
    client: [],
    country: [],
    stockLocation: [],
    shippingPhone: '',
    hasOrderErrors: false,
    generalErrorIds: [],
  };

  // Agrupar condiciones por columna
  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((condition) => {
    if (!byColumn[condition.column]) {
      byColumn[condition.column] = [];
    }
    byColumn[condition.column].push(condition);
  });

  // Helper para parsear rangos de fechas
  const parseDateRange = (column: string): DateRange | undefined => {
    const dateConditions = byColumn[column] || [];
    const dateFrom = dateConditions.find((c) => c.action === 'greater_equal');
    const dateTo = dateConditions.find((c) => c.action === 'less_equal');
    if (dateFrom?.value && dateTo?.value) {
      return {
        // IMPORTANTE: `YYYY-MM-DD` se interpreta como UTC si usamos `new Date(string)`,
        // lo que puede desplazar la fecha (ej: HOY -> AYER en GMT-3/4).
        // Por eso parseamos como fecha local.
        from: parseDateForState(dateFrom.value),
        to: parseDateForState(dateTo.value),
      };
    }
    return undefined;
  };

  // Parsear fechas de la API como fecha local para evitar desfase por timezone.
  // La API envía `YYYY-MM-DD` (generado con `getFullYear/getMonth/getDate`), así que soportamos ese formato.
  function parseDateForState(value: unknown): Date {
    if (typeof value !== 'string') return new Date(value as any);

    const str = value.trim();
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, d] = match.map(Number);
      return new Date(y, m - 1, d);
    }

    return new Date(str);
  }

  // Fechas
  state.processedAt = parseDateRange('processed_at');
  state.createdAt = parseDateRange('order_created_at');
  state.trackingUpdateAt = parseDateRange('date_tracking_update');

  // Arrays con operador 'in'
  const getArrayValue = (column: string): string[] => {
    const condition = byColumn[column]?.find((c) => c.action === 'in');
    if (condition?.value) {
      return Array.isArray(condition.value) ? condition.value : [condition.value];
    }
    return [];
  };

  state.logisticStatus = getArrayValue('shipment_status_id');
  state.shop = getArrayValue('shop_id');
  state.paymentMethod = getArrayValue('payment_method_id');
  state.products = getArrayValue('product_id');
  state.courier = getArrayValue('courier_id');
  state.client = getArrayValue('team_id');
  state.country = getArrayValue('country_id');
  state.stockLocation = getArrayValue('stock_location_orders_id');

  // Teléfono de envío (contains)
  const phoneCondition = byColumn['shipping_phone']?.find((c) => c.action === 'contains');
  state.shippingPhone = (phoneCondition?.value as string) || '';

  // Pedidos con errores
  state.hasOrderErrors = !!byColumn['has_order_errors']?.find((c) => c.action === 'equal');

  // Tipos de error
  state.generalErrorIds = getArrayValue('general_error_ids');

  return state;
}

/**
 * Formatea una fecha para la API (formato YYYY-MM-DD)
 * Usa métodos locales para evitar problemas de timezone
 */
function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
